import {verifiedFetch} from '@github-ui/verified-fetch'
import {XIcon} from '@primer/octicons-react'
import {IconButton, Link} from '@primer/react'
import {type ReactNode, useState} from 'react'
import type {BannerSegment, BannerAnalytics} from '@github-ui/code-view-types'
import styles from './BannerNudgeCard.module.css'

export interface BannerNudgeAnalytics extends BannerAnalytics {
  ctaAction: string
  entityId?: number
}

export interface BannerNudgeCardProps {
  title: string
  segments: BannerSegment[]
  icon: ReactNode
  ctaTitle: string
  ctaHref: string
  ctaPrimary?: boolean
  stacked?: boolean
  dismissPath: string
  analytics: BannerNudgeAnalytics
  testSelector: string
}

function getSegmentKey(segment: BannerSegment, index: number): string {
  return `${segment.type}-${index}-${segment.content.slice(0, 20)}`
}

export function BannerNudgeCard({
  title,
  segments,
  icon,
  ctaTitle,
  ctaHref,
  ctaPrimary = true,
  stacked = false,
  dismissPath,
  analytics,
  testSelector,
}: BannerNudgeCardProps) {
  const [hidden, setHidden] = useState(false)

  const handleDismiss = async () => {
    try {
      await verifiedFetch(dismissPath, {method: 'POST'})
    } catch {
      // Silently handle network errors to maintain good UX
    } finally {
      setHidden(true)
    }
  }

  if (hidden) {
    return null
  }

  return (
    <>
      <span data-test-selector={testSelector} />
      <div
        className="ipm-nudge-box settings Box-header"
        style={
          !stacked
            ? {
                borderBottom: 'var(--borderWidth-thin) solid var(--borderColor-default)',
                borderTopLeftRadius: 'var(--borderRadius-medium)',
                borderTopRightRadius: 'var(--borderRadius-medium)',
              }
            : undefined
        }
        data-testid="task-nudge-card"
        data-analytics-visible={JSON.stringify({
          category: analytics.category,
          action: 'visible',
          label: `group:${analytics.group};location:${analytics.location}`,
        })}
      >
        <div className={styles.IconWrapper} aria-hidden="true">
          {icon}
        </div>

        <div className="ipm-nudge-content">
          <h2>{title}</h2>
          <p>
            {segments.map((segment, index) =>
              segment.type === 'link' && 'href' in segment ? (
                <Link key={getSegmentKey(segment, index)} href={segment.href} className="Link--inTextBlock">
                  {segment.content}
                </Link>
              ) : (
                <span key={getSegmentKey(segment, index)}>{segment.content}</span>
              ),
            )}
          </p>
        </div>

        <a
          href={ctaHref}
          className={`ipm-nudge-cta btn btn-sm${ctaPrimary ? ' btn-primary' : ''}`}
          data-test-selector={testSelector}
          data-analytics-event={JSON.stringify({
            category: analytics.category,
            action: analytics.ctaAction,
            label: `group:${analytics.group};location:${analytics.location}`,
          })}
        >
          {ctaTitle}
        </a>

        <div className="ipm-nudge-dismiss">
          <IconButton
            icon={XIcon}
            variant="invisible"
            aria-label="Dismiss"
            onClick={handleDismiss}
            data-analytics-event={JSON.stringify({
              category: analytics.category,
              action: 'dismiss',
              label: `group:${analytics.group};location:${analytics.location}`,
            })}
          />
        </div>
      </div>
    </>
  )
}
